import { Compartment } from '@codemirror/state'
import { useTheme } from '@mui/material'
import CodeMirror, { EditorState, EditorView, ReactCodeMirrorProps } from '@uiw/react-codemirror'
import { CSSProperties, memo, useRef } from 'react'
import { useAppSelector } from '../../../store/Hooks'

interface P extends ReactCodeMirrorProps {
    disabled?: boolean
    error?: boolean
    width?: string
    style?: CSSProperties
}

const NodeCodemirrorTextEditor = ({ disabled = false, error = false, width = '100%', style, ...props }: P) => {
    const themeMode = useAppSelector(state => state.app.mode)
    const theme = useTheme()

    const wrapCompartment = useRef(new Compartment())

    const viewRef = useRef<EditorView | null>(null)

    const wrapToggle = EditorView.domEventHandlers({
        focus: () => {
            if (viewRef.current) {
                viewRef.current.dispatch({
                    effects: wrapCompartment.current.reconfigure(EditorView.lineWrapping)
                })
            }
        },
        blur: () => {
            if (viewRef.current) {
                viewRef.current.dispatch({
                    effects: wrapCompartment.current.reconfigure([])
                })
            }
        }
    })

    const singleLine = EditorState.transactionFilter.of(tr => {
        if (tr.changes.empty) return tr
        if (tr.newDoc.lines > 1 && !tr.isUserEvent('input.paste')) return []

        const removeNLs = []
        tr.changes.iterChanges((_fromA, _toA, fromB, _toB, ins) => {
            const lineIter = ins.iterLines().next()
            if (ins.lines <= 1) return

            let len = fromB + lineIter.value.length
            lineIter.next()
            for (; !lineIter.done; lineIter.next()) {
                removeNLs.push({ from: len, to: len + 1 })
                len += lineIter.value.length + 1
            }
        })

        return [tr, { changes: removeNLs, sequential: true }]
    })

    const cmStyles = EditorView.theme({
        '&': {
            border: `1px solid ${
                error
                    ? theme.palette.error.main
                    : disabled
                        ? 'rgba(255, 255, 255, 0.2)'
                        : themeMode === 'light'
                            ? 'rgba(0, 0, 0, 0.2)'
                            : 'rgba(255, 255, 255, 0.2)'
            }`
        },
        '&:hover:not(.cm-focused)': {
            border: `1px solid ${
                error
                    ? undefined
                    : disabled
                        ? 'none'
                        : themeMode === 'light'
                            ? 'rgba(0, 0, 0, 0.8)'
                            : 'rgba(255, 255, 255, 0.8)'
            }`
        },
        '&.cm-focused': {
            border: `1px solid ${
                error ? undefined : disabled ? 'none' : theme.palette.primary.main
            }`
        },
        '&.cm-editor': {
            borderRadius: 'var(--unit)',
            padding: 'calc(var(--unit) / 1.4)',
            backgroundColor: 'inherit'
        },
        '.cm-content': {
            opacity: disabled ? 0.5 : undefined
        },
        '.cm-scroller': {
            overflowX: 'hidden'
        },
        '.cm-cursor': disabled ? { visibility: 'hidden' } : {}
    })

    return (
        <div style={{ width: '100%', overflow: 'hidden' }}>
            <CodeMirror
                theme={themeMode}
                style={{ ...style }}
                width={width}
                basicSetup={{
                    lineNumbers: false,
                    foldGutter: false,
                    highlightActiveLine: false
                }}
                readOnly={disabled}
                {...props}
                onCreateEditor={view => {
                    viewRef.current = view
                }}
                extensions={[
                    cmStyles,
                    wrapToggle,
                    wrapCompartment.current.of([]),
                    singleLine,
                    ...(props.extensions ?? [])
                ]}
            />
        </div>
    )
}

export default memo(NodeCodemirrorTextEditor)
